import { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { ApiError } from '@/lib/api';
import {
  getComplaintWorkbenchAttachmentUrlApi,
  type ComplaintDetail,
} from '@/lib/complaints-api';
import {
  availableComplaintActions,
  COMPLAINT_CATEGORY_LABELS,
  COMPLAINT_CONCLUSION_LABELS,
  COMPLAINT_STATUS_LABELS,
  complaintActionLabel,
  formatComplaintDate,
  maskEmployeeId,
  type ComplaintActionMode,
} from '@/lib/complaints-model';

interface Props {
  detail: ComplaintDetail;
  onClose: () => void;
  onOpenAction: (mode: ComplaintActionMode) => void;
}

export function ComplaintDetailModal({ detail, onClose, onOpenAction }: Props) {
  const [attachmentBusyId, setAttachmentBusyId] = useState<string | null>(null);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const actions = availableComplaintActions(detail);

  async function openAttachment(attachmentId: string) {
    setAttachmentBusyId(attachmentId);
    setAttachmentError(null);
    try {
      // 每次点击都现取签名 URL；只保存在当前调用栈，不写 state、日志或长期存储。
      const signed = await getComplaintWorkbenchAttachmentUrlApi(
        detail.id,
        attachmentId,
      );
      if (signed.expiresIn <= 0 || signed.expiresIn > 300) {
        throw new ApiError('附件临时地址有效期不符合 ≤300 秒契约', 502);
      }
      // noopener 模式下 window.open 的返回值可能为 null，即使窗口已成功打开；
      // 因此不把返回值当成功判据，也不把临时 URL 放进组件 state。
      window.open(signed.url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      setAttachmentError(toDetailError(error));
    } finally {
      setAttachmentBusyId(null);
    }
  }

  return (
    <Modal
      title={`投诉详情 · ${detail.complaintNo}`}
      description={`${COMPLAINT_CATEGORY_LABELS[detail.category]} · ${COMPLAINT_STATUS_LABELS[detail.status]} · version ${detail.version}`}
      onClose={onClose}
      wide
      footer={
        <>
          {actions.map((mode) => (
            <button
              key={mode}
              type="button"
              className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
              onClick={() => onOpenAction(mode)}
              data-testid={`complaint-action-${mode}`}
            >
              {complaintActionLabel(mode)}
            </button>
          ))}
          <button
            type="button"
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            onClick={onClose}
          >
            关闭
          </button>
        </>
      }
    >
      <div className="max-h-[70vh] space-y-5 overflow-y-auto pr-1">
        <section className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <h3 className="font-semibold text-slate-900">{detail.title}</h3>
          <p className="mt-1 text-xs text-slate-500">
            子类：{detail.subcategory || '—'} · 创建：{formatComplaintDate(detail.createdAt)} ·
            截止：{formatComplaintDate(detail.dueAt)}
          </p>
          <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-slate-700">
            {detail.body}
          </p>
        </section>

        <section className="grid gap-3 sm:grid-cols-2">
          <Info label="提交人" value={detail.submitter.name || maskEmployeeId(detail.submitter.employeeId)} />
          <Info label="联系方式" value={contactText(detail)} />
          <Info
            label="当前处理人"
            value={
              detail.currentHandler?.name ||
              maskEmployeeId(detail.currentHandler?.employeeId)
            }
          />
          <Info
            label="处理结论"
            value={
              detail.conclusion
                ? COMPLAINT_CONCLUSION_LABELS[detail.conclusion]
                : '—'
            }
          />
        </section>

        <section>
          <h3 className="text-sm font-semibold text-slate-900">官方回复</h3>
          <p className="mt-2 whitespace-pre-wrap rounded-lg bg-blue-50 p-3 text-sm text-blue-900">
            {detail.officialReply || '尚无官方回复'}
          </p>
        </section>

        <section>
          <h3 className="text-sm font-semibold text-slate-900">
            私有附件（{detail.attachments.length}）
          </h3>
          <div className="mt-2 space-y-2">
            {detail.attachments.length === 0 ? (
              <p className="text-sm text-slate-500">无附件</p>
            ) : null}
            {detail.attachments.map((attachment) => (
              <button
                key={attachment.id}
                type="button"
                onClick={() => void openAttachment(attachment.id)}
                disabled={attachmentBusyId === attachment.id}
                className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-left text-sm hover:bg-slate-50 disabled:opacity-60"
              >
                <span>
                  {attachment.mime} · {formatBytes(attachment.bytes)} ·{' '}
                  {formatComplaintDate(attachment.createdAt)}
                </span>
                <span className="inline-flex items-center gap-1 text-brand-600">
                  {attachmentBusyId === attachment.id ? '获取中…' : '临时打开'}
                  <ExternalLink className="h-4 w-4" />
                </span>
              </button>
            ))}
          </div>
          {attachmentError ? (
            <p role="alert" className="mt-2 text-sm text-rose-700">
              {attachmentError}
            </p>
          ) : null}
          <p className="mt-1 text-xs text-slate-500">
            附件地址每次由后端签发且最长 5 分钟；页面不会保存或记录该地址。
          </p>
        </section>

        <section>
          <h3 className="text-sm font-semibold text-slate-900">不可变处理时间线</h3>
          <ol className="mt-2 space-y-3">
            {detail.events.map((event) => (
              <li key={event.id} className="rounded-lg border border-slate-200 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-medium text-slate-800">
                    {event.type} · version {event.version}
                  </span>
                  <span className="text-xs text-slate-500">
                    {formatComplaintDate(event.createdAt)}
                  </span>
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  操作者：{maskEmployeeId(event.operatorEmployeeId)}（{event.operatorRole}）
                  {event.fromStatus || event.toStatus
                    ? ` · ${event.fromStatus || '—'} → ${event.toStatus || '—'}`
                    : ''}
                </p>
                {event.summary ? (
                  <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">
                    {event.summary}
                  </p>
                ) : null}
              </li>
            ))}
          </ol>
        </section>
      </div>
    </Modal>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 text-sm text-slate-800">{value}</div>
    </div>
  );
}

function contactText(detail: ComplaintDetail): string {
  return [detail.submitter.phone, detail.submitter.wechat, detail.submitter.email]
    .filter((value): value is string => !!value)
    .join(' / ') || '—';
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function toDetailError(error: unknown): string {
  if (error instanceof ApiError) {
    return error.status > 0
      ? `${error.message}（HTTP ${error.status}）`
      : error.message;
  }
  return error instanceof Error ? error.message : '附件打开失败。';
}
